/**
 * 2026-07-05: 浏览器 console → 本地文件转发
 *
 * 用途:用户在浏览器测 prototype 时,console.log/info/warn/error 自动写
 *      到 prototype/logs/browser.log(由 vite plugin 的 /api/log 端点落盘)
 *      AI 可直接 read,用户不用手动复制截图
 *
 * 覆盖:
 * - page console.* → POST /api/log
 * - window error / unhandledrejection → POST /api/log
 * - Worker [BC-DIAG] 日志 → worker postMessage('diag_log') → main thread
 *   console.*(主线程已 patch)→ POST /api/log
 *
 * 限制:
 * - 仅 dev 模式有效(prod build 没 vite plugin)
 * - 批量 100ms 发送一次(page console 高频时合并)
 * - 10000 行 cap(由 vite plugin 处理,自动 rotate)
 *
 * 2026-07-08 重大修改(Fix browser.log hang):
 *   - 原实现优先 sendBeacon,它在活跃页面中"可能"只入队不立即发送,导致 browser.log
 *     长期空白
 *   - 改为优先 fetch(浏览器 Promise 保证立即发),pagehide 时改用 sendBeacon 兜底
 *     (此时 fetch 会被 abort)
 */

const ENDPOINT = '/api/log';
const BATCH_MS = 100;
const MAX_QUEUE = 200;

let queue: string[] = [];
let timer: number | null = null;
let enabled = false;

function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_QUEUE);
  const body = batch.join('\n') + '\n';

  // 2026-07-08 修复:优先 fetch(有 Promise 反馈,保证立即发送),
  //   pagehide 时才用 sendBeacon 兜底(此时 fetch 会被浏览器 abort)
  fetch(ENDPOINT, { method: 'POST', body, keepalive: true })
    .then((r) => {
      if (!r.ok) origWarn(`[console-logger] POST /api/log HTTP ${r.status}`);
    })
    .catch((e: any) => {
      origWarn(`[console-logger] fetch /api/log failed: ${e?.message ?? e}, trying sendBeacon`);
      // 降级:sendBeacon(可能被浏览器限制,但至少尝试)
      try {
        const blob = new Blob([body], { type: 'text/plain' });
        const sent = navigator.sendBeacon(ENDPOINT, blob);
        if (!sent) {
          origError(`[console-logger] all POST methods failed, ${batch.length} log lines LOST`);
        }
      } catch (be: any) {
        origError(`[console-logger] all POST methods threw: ${be?.message ?? be}, ${batch.length} lines LOST`);
      }
    });
}

function scheduleFlush() {
  if (timer !== null) return;
  timer = window.setTimeout(() => {
    timer = null;
    flush();
  }, BATCH_MS);
}

function format(args: any[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.message}\n${a.stack ?? ''}`;
      if (typeof a === 'object' && a !== null) {
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      }
      return String(a);
    })
    .join(' ');
}

function write(level: string, args: any[]) {
  if (!enabled) return;
  const msg = format(args);
  const lines = msg.split('\n');
  queue.push(`[${level}] ${lines[0]}`);
  for (let i = 1; i < lines.length; i++) {
    queue.push(`[${level}.cont] ${lines[i]}`);
  }
  scheduleFlush();
}

// ===== Patch console.* =====
const origLog = console.log.bind(console);
const origInfo = console.info.bind(console);
const origWarn = console.warn.bind(console);
const origError = console.error.bind(console);

console.log = (...args: any[]) => { origLog(...args); write('log', args); };
console.info = (...args: any[]) => { origInfo(...args); write('info', args); };
console.warn = (...args: any[]) => { origWarn(...args); write('warn', args); };
console.error = (...args: any[]) => { origError(...args); write('error', args); };

// ===== 全局错误兜底 =====
window.addEventListener('error', (e) => {
  write('window.error', [`${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`]);
});
window.addEventListener('unhandledrejection', (e) => {
  const reason = e.reason instanceof Error
    ? `${e.reason.message}\n${e.reason.stack ?? ''}`
    : String(e.reason);
  write('unhandledrejection', [reason]);
});

// ===== 离开页面时用 sendBeacon 兜底(此时 fetch 会被浏览器 abort) =====
window.addEventListener('pagehide', () => {
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_QUEUE);
  const body = batch.join('\n') + '\n';
  try {
    const blob = new Blob([body], { type: 'text/plain' });
    navigator.sendBeacon(ENDPOINT, blob);
  } catch { /* silent */ }
});

/**
 * 在 main.ts 启动时调用,开启转发。
 * 默认禁用(避免 import side-effect 太强),调用后才生效。
 */
export function startConsoleLogger() {
  enabled = true;
  origLog(`[console-logger] enabled → POST ${ENDPOINT} → prototype/logs/browser.log`);
}

/**
 * 强制 flush 队列(供测试或调试用)
 */
export function flushLogsNow() {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  flush();
}
